import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, MapPin, Trophy, Users, Gamepad2, ArrowLeft, UserPlus, Share2, Clock, Swords, FileDown, Layers } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl, API } from "@/lib/api";
import { STATUS_STYLE } from "./Tournaments";
import BracketView, { TeamLogo } from "@/components/tournament/BracketView";
import TeamsList from "@/components/tournament/TeamsList";
import ChampionBanner from "@/components/tournament/ChampionBanner";
import { FORMAT_LABEL, fmtDateTime } from "@/components/tournament/constants";

const TABS = ["Overview", "Matches", "Bracket", "Teams", "News", "Rules"];
const isReal = (m) => ![m.a, m.b].some((x) => x === "TBD" || x === "BYE" || /^Slot \d+$/.test(x));

function LiveMatch({ m, logos, fmt }) {
  return (
    <div className="card-surface rounded-2xl p-6 relative overflow-hidden" data-testid="live-match">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-white px-2.5 py-1 rounded live-dot" style={{ background: "hsl(var(--accent))" }}><span className="w-1.5 h-1.5 rounded-full bg-white" /> LIVE</span>
        <span className="text-xs text-white/50">{m.round_name}{m.group ? ` · Group ${m.group}` : ""} · {fmt}</span>
      </div>
      <div className="mt-6 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        {["a", "b"].map((side, i) => (
          <div key={side} className={`flex flex-col items-center gap-2 text-center ${i === 1 ? "order-3" : ""}`}>
            <TeamLogo team={m[side]} logos={logos} size="w-14 h-14 text-xl" />
            <span className="font-bold text-white text-sm sm:text-base break-words">{m[side]}</span>
          </div>
        ))}
        <div className="order-2 text-4xl sm:text-5xl font-bold font-head text-white whitespace-nowrap">{m.sa} <span className="text-white/30">-</span> {m.sb}</div>
      </div>
    </div>
  );
}

function MatchRow({ m }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 text-sm">
      <span className="w-28 shrink-0 text-xs text-white/50 flex items-center gap-1.5"><Clock size={12} /> {m.scheduled_at ? fmtDateTime(m.scheduled_at) : "TBD"}</span>
      <span className={`flex-1 truncate text-right ${m.winner === m.a ? "text-white font-semibold" : "text-white/75"}`}>{m.a}</span>
      <span className="w-14 text-center font-head text-white shrink-0">{m.status === "upcoming" ? "vs" : `${m.sa} - ${m.sb}`}</span>
      <span className={`flex-1 truncate ${m.winner === m.b ? "text-white font-semibold" : "text-white/75"}`}>{m.b}</span>
      <span className={`hidden sm:block w-28 text-right text-[11px] font-bold uppercase truncate ${m.status === "live" ? "text-[hsl(var(--accent))]" : "text-white/40"}`}>{m.status === "live" ? "● Live" : m.round_name}</span>
    </div>
  );
}

function Overview({ t, logos, setTab }) {
  const ms = (t.bracket?.matches || []).filter(isReal);
  const live = ms.filter((m) => m.status === "live");
  const next = ms.filter((m) => m.status === "upcoming").sort((a, b) => (a.scheduled_at || "9999").localeCompare(b.scheduled_at || "9999")).slice(0, 5);
  return (
    <div className="space-y-6">
      {t.result?.champion && <ChampionBanner result={t.result} name={t.name} slug={t.slug} />}
      {live.map((m) => <LiveMatch key={m.id} m={m} logos={logos} fmt={t.match_format} />)}
      <div className="card-surface rounded-2xl overflow-hidden" data-testid="upcoming-matches">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5"><h3 className="font-bold text-white">Upcoming Matches</h3><button onClick={() => setTab("Matches")} className="text-xs font-semibold text-[hsl(var(--primary))] hover:underline whitespace-nowrap">See all</button></div>
        {next.length ? <div className="divide-y divide-white/5">{next.map((m) => <MatchRow key={m.id} m={m} />)}</div> : <div className="px-5 py-8 text-sm text-white/40 text-center">{t.status === "completed" ? "All matches have been played." : "Matches appear here once teams are placed in the bracket."}</div>}
      </div>
      {t.description && <div className="card-surface rounded-2xl p-6"><h3 className="font-bold text-white mb-2">About this tournament</h3><p className="text-white/70 leading-relaxed whitespace-pre-line">{t.description}</p></div>}
    </div>
  );
}

function NewsList({ items }) {
  if (!items.length) return <div className="text-white/40 py-12 text-center">No news yet.</div>;
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {items.map((a) => (
        <Link key={a.id} to={`/news/${a.slug}`} className="group flex gap-3 card-surface rounded-xl p-3 hover:bg-white/[0.04]">
          <img src={mediaUrl(a.cover)} alt="" className="w-24 h-16 rounded-lg object-cover shrink-0" />
          <div className="min-w-0"><span className="eyebrow" style={{ color: "hsl(var(--accent))" }}>{a.category}</span><h4 className="text-sm font-semibold text-white line-clamp-2 group-hover:underline">{a.title}</h4></div>
        </Link>
      ))}
    </div>
  );
}

export default function TournamentDetail() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [t, setT] = useState(null);
  const [news, setNews] = useState([]);
  const [tab, setTab] = useState(params.get("registered") ? "Teams" : "Overview");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/tournaments/${slug}`).then((r) => setT(r.data)).catch(() => setNotFound(true));
    api.get("/news", { params: { limit: 6 } }).then((r) => setNews(r.data.items)).catch(() => {});
  }, [slug]);

  if (notFound) return <div className="py-24 text-center text-white"><h1 className="text-3xl font-bold">Tournament not found</h1><Link to="/tournaments" className="mt-4 inline-block text-[hsl(var(--primary))]">← Back</Link></div>;
  if (!t) return <div className="py-24 text-center text-white/40">Loading…</div>;

  const st = STATUS_STYLE[t.status] || STATUS_STYLE.upcoming;
  const confirmed = (t.participants || []).filter((p) => p.status === "confirmed");
  const logos = Object.fromEntries(confirmed.map((p) => [p.name, p.logo]));
  const share = () => (navigator.share ? navigator.share({ title: t.name, url: window.location.href }).catch(() => {}) : navigator.clipboard.writeText(window.location.href).then(() => toast.success("Link copied")));
  const btn = "inline-flex items-center justify-center gap-2 whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-bold transition-colors";
  const info = [[Users, `${confirmed.length} / ${t.max_teams} Teams`], [Layers, FORMAT_LABEL[t.format]], [Swords, t.match_format], [Trophy, `Grand Final ${t.grand_final_format}`], [MapPin, t.online ? "Online" : t.location]];

  return (
    <div data-testid="tournament-detail">
      <div className="relative h-[52vh] min-h-[380px] max-h-[560px] overflow-hidden">
        {t.cover && <motion.img initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 1.6, ease: "easeOut" }} src={mediaUrl(t.cover)} alt={t.name} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--background))] via-transparent to-transparent" />
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative z-10 h-full mx-auto max-w-7xl px-4 md:px-6 flex flex-col justify-end pb-10">
          <Link to="/tournaments" className="eyebrow text-white/60 hover:text-white flex items-center gap-1.5 mb-4 self-start"><ArrowLeft size={13} /> Tournaments</Link>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`eyebrow px-2.5 py-1 rounded text-white ${st.pulse ? "live-dot" : ""}`} style={{ background: st.color }} data-testid="tdetail-status">{st.label}</span>
            {t.game && <span className="eyebrow px-2.5 py-1 rounded bg-white/10 backdrop-blur text-white flex items-center gap-1.5"><Gamepad2 size={12} /> {t.game}</span>}
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] max-w-4xl break-words" data-testid="tdetail-title">{t.name}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-white/70">
            <span className="flex items-center gap-1.5"><Calendar size={14} /> {fmtDateTime(t.start_date)} – {fmtDateTime(t.end_date)}</span>
            <span className="flex items-center gap-1.5"><Clock size={14} /> {t.timezone}</span>
          </div>
          <div className="mt-5 flex flex-wrap gap-2.5">
            {t.registration_open && <Link to={`/tournaments/${t.slug}/register`} data-testid="register-team-btn" className={`${btn} bg-[hsl(var(--primary))] text-white hover:brightness-110`}><UserPlus size={16} /> Register Team</Link>}
            {t.status === "completed" && <a href={`${API}/tournaments/${t.slug}/report.pdf`} data-testid="tdetail-report" className={`${btn} bg-white text-black hover:bg-white/90`}><FileDown size={16} /> Download Report (PDF)</a>}
            <button onClick={share} data-testid="tdetail-share" className={`${btn} border border-white/25 bg-white/5 backdrop-blur text-white hover:bg-white/10`}><Share2 size={15} /> Share</button>
          </div>
        </motion.div>
      </div>

      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="card-surface rounded-2xl px-5 py-4 flex flex-wrap gap-x-8 gap-y-3 -mt-2 relative z-10" data-testid="tdetail-infobar">
          {info.map(([Icon, label]) => <span key={label} className="flex items-center gap-2 text-sm font-semibold text-white/80 whitespace-nowrap"><Icon size={16} className="text-[hsl(var(--primary))]" /> {label}</span>)}
          {t.prize_pool && <span className="sm:ml-auto flex items-center gap-2 text-sm font-bold text-white whitespace-nowrap"><Trophy size={16} className="text-yellow-400" /> {t.prize_pool}</span>}
        </div>

        <div className="flex gap-1 border-b border-white/10 mt-8 mb-6 overflow-x-auto">
          {TABS.map((x) => (
            <button key={x} onClick={() => setTab(x)} data-testid={`tdetail-tab-${x.toLowerCase()}`}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === x ? "border-[hsl(var(--primary))] text-white" : "border-transparent text-white/50 hover:text-white"}`}>
              {x}{x === "Teams" ? ` (${confirmed.length})` : ""}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1fr_340px] gap-8 pb-16">
          <div className="min-w-0">
            {tab === "Overview" && <Overview t={t} logos={logos} setTab={setTab} />}
            {tab === "Matches" && (((t.bracket?.matches || []).filter(isReal).length) ? <div className="card-surface rounded-2xl divide-y divide-white/5" data-testid="public-schedule">{(t.bracket.matches.filter(isReal)).sort((a, b) => (a.scheduled_at || "9999").localeCompare(b.scheduled_at || "9999") || a.round - b.round).map((m) => <MatchRow key={m.id} m={m} />)}</div> : <div className="text-white/40 py-12 text-center">The match schedule will appear here.</div>)}
            {tab === "Bracket" && <div className="card-surface rounded-2xl p-5"><BracketView tournament={t} /></div>}
            {tab === "Teams" && <TeamsList participants={confirmed} />}
            {tab === "News" && <NewsList items={news} />}
            {tab === "Rules" && (t.terms ? <div className="card-surface rounded-2xl p-6 text-sm text-white/70 whitespace-pre-line leading-relaxed" data-testid="tdetail-rules">{t.terms}</div> : <div className="text-white/40 py-12 text-center">No rules published yet.</div>)}
          </div>
          <aside className="space-y-5">
            <div className="card-surface rounded-2xl p-5">
              <h3 className="font-bold text-white mb-3">Recent News</h3>
              <div className="space-y-4">
                {news.slice(0, 3).map((a) => (
                  <Link key={a.id} to={`/news/${a.slug}`} className="group block">
                    <img src={mediaUrl(a.cover)} alt="" className="w-full h-28 object-cover rounded-xl" />
                    <h4 className="mt-2 text-sm font-semibold text-white line-clamp-2 group-hover:underline">{a.title}</h4>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
